// Hover help: the contrast experiment's settings.

import { ChartLevelsPicture, ContrastStaircases, GatePicture, LetterSizePicture, PriorPicture, PsychometricPicture,
  SimNumbers, contrastOf } from "./pictures";
import type { HelpContext, HelpEntry } from "./types";

const SIM_SOURCE = "Suggested numbers: 150 simulated tests each, of a patient at log CS 1.65, with this app's staircase "
  + "engine (logistic observer, slope 15 per log unit, 25% guessing, 2% lapses). SD: of the estimate from test to test. "
  + "Up/down staircases read about 0.04–0.07 log units stronger than the point they aim at (reversal averaging); "
  + "a constant offset does not affect change over time.";

const m = (body: string) => `<math display="block">${body}</math>`;
const frac = (a: string, b: string) => `<mfrac><mrow>${a}</mrow><mrow>${b}</mrow></mfrac>`;
const mi = (s: string) => `<mi>${s}</mi>`;
const mn = (s: string | number) => `<mn>${s}</mn>`;
const mo = (s: string) => `<mo>${s}</mo>`;
const sub = (a: string, b: string) => `<msub>${a}<mtext>${b}</mtext></msub>`;

/** The staircase animation plus the live numbers for the current settings. */
const sc = (highlight: Parameters<typeof ContrastStaircases>[0]["highlight"]) => (ctx: HelpContext) => (
  <>
    <ContrastStaircases ctx={ctx} highlight={highlight} />
    <SimNumbers ctx={ctx} kind="contrast" />
  </>
);

export const CONTRAST_HELP: Record<string, HelpEntry> = {
  "contrast.method": {
    title: "Contrast method",
    what: "How the faintest letter the patient can see is found.\n\n"
      + "Interleaved staircases (the default): several staircases run at once, taking turns at random. Each one makes the "
      + "letter fainter after right answers and stronger after wrong ones, so it settles where the patient is right a set "
      + "share of the time. Their thresholds are averaged; how much they disagree says how reliable the result is.\n\n"
      + "Chart levels (Pelli-Robson): fixed levels 0.15 log CS apart, a group of letters at each, going fainter until a "
      + "level is failed, like the paper chart. Results compare directly with a clinic's chart.\n\n"
      + "QUEST pair: the older Bayesian method, kept for programs made before the staircases.",
    picture: sc("threshold"),
    scenarios: [
      { scenario: "Monitoring change at home", value: "Interleaved staircases", why: "finer than the chart's 0.15 steps; reports its own reliability" },
      { scenario: "Comparing with clinic Pelli-Robson charts", value: "Chart levels", why: "same levels and scoring rule as the chart" },
    ],
  },
  "contrast.size_rule": {
    title: "Letter size for the contrast test",
    what: "Contrast sensitivity depends on letter size, so the size is fixed for the whole test.\n\n"
      + "The chart's size (2.8° high, the Pelli-Robson letters at 1 m) is the standard. Acuity + offset sizes the letters "
      + "from this eye's acuity measured earlier in the same program, for patients who cannot see 2.8° letters well, or "
      + "to measure contrast at a set distance above the acuity limit. It needs an acuity experiment before it in the program.",
    math: m(`${mi("size")}${mo("=")}${sub(mi("logMAR"), "acuity")}${mo("+")}${mi("offset")}`),
    mathNote: "Each 0.1 logMAR is 26% larger; +0.3 is twice the acuity letter's size.",
    picture: (ctx) => { const c = contrastOf(ctx); return <LetterSizePicture rule={c.size_rule} offset={c.acuity_offset_logmar} />; },
    scenarios: [
      { scenario: "Standard (comparable with the chart)", value: "2.8°", why: "the Pelli-Robson letter size" },
      { scenario: "Low vision (acuity worse than about 0.8 logMAR)", value: "acuity + 0.3", why: "2.8° letters may be near the acuity limit" },
      { scenario: "Contrast near the resolution limit", value: "acuity + 0.1 to 0.2", why: "tests contrast where letters are just resolvable" },
    ],
  },
  "contrast.n_staircases": {
    title: "Number of staircases",
    what: "How many staircases run side by side. Each starts at a different contrast, spread evenly from the easy start to "
      + "the hard one. The patient cannot tell which staircase a letter belongs to, so they cannot anticipate the next "
      + "letter. The result is the mean of the staircases' thresholds, and their spread (SD) is reported: a large spread "
      + "says the result is worth repeating.\n\nMore staircases: a better average and a better spread estimate, but more trials.",
    math: m(`${mi("log CS")}${mo("=")}${frac(mn(1), mi("n"))}<munderover>${mo("∑")}<mrow>${mi("i")}${mo("=")}${mn(1)}</mrow>${mi("n")}</munderover>${sub(mi("T"), "i")}`),
    mathNote: "Tᵢ: each staircase's threshold; the SD between them is reported with the result.",
    range: "1–8; default 4.",
    picture: sc("start"),
    scenarios: [
      { scenario: "Quick home check", value: "4 × 12, balanced", why: "48 trials per eye; SD about 0.085" },
      { scenario: "Standard monitoring (default)", value: "4 × 15", why: "60 trials per eye; SD about 0.06" },
      { scenario: "Research precision", value: "4, stop when settled (6 reversals, at most 30)", why: "about 77 trials; SD about 0.04" },
      { scenario: "Unpredictable order, with checks", value: "6 × 15, staggered, balanced, 10% checks", why: "about 99 trials; SD about 0.06" },
    ],
    source: SIM_SOURCE,
  },
  "contrast.trials_per_staircase": {
    title: "Trials per staircase",
    what: "How many letters each staircase shows. Total trials per eye = staircases × this (plus any attention checks). "
      + "With \"stop when settled\" this becomes the most each may take.\n\nThe first few trials of each staircase are "
      + "spent walking down from its start; precision comes from the trials after that, around the threshold.",
    math: m(`${mi("N")}${mo("≈")}${mi("n")}${mo("×")}${sub(mi("t"), "staircase")}${mo("+")}${mi("r")}${mo("×")}${mi("N")}`),
    mathNote: "r: the attention-check rate. Precision improves roughly as 1/√(trials near threshold).",
    range: "5–60; default 15.",
    picture: sc("count"),
    scenarios: [
      { scenario: "Fatigue-prone patients", value: "12", why: "48 trials per eye with 4 staircases; SD about 0.085" },
      { scenario: "Default", value: "15", why: "60 trials per eye; SD about 0.06" },
      { scenario: "Research", value: "20–25", why: "lower SD (roughly as 1/√trials); a longer test" },
    ],
    source: SIM_SOURCE,
  },
  "contrast.staircase_type": {
    title: "Staircase type",
    what: "The rule each staircase follows after an answer.\n\n"
      + "Weighted up/down (Kaernbach, default): one step fainter after a right answer, a bigger step stronger after a wrong "
      + "one; the ratio sets the share right it settles at (75% by default). Every trial counts.\n\n"
      + "Levitt n-down/1-up: fainter only after n right in a row, stronger after any wrong: settles at 70.7% (n = 2), 79.4% (3), "
      + "84.1% (4).\n\nSimple 1-up/1-down: settles at 50%, close to guessing for a 4-way E: not recommended.\n\n"
      + "QUEST: a Bayesian staircase that puts each letter at its best current guess of the threshold. Efficient, but it "
      + "relies on its assumptions about the patient's psychometric function.",
    math: m(`${frac(sub(mi("Δ"), "up"), sub(mi("Δ"), "down"))}${mo("=")}${frac(mi("p"), `${mn(1)}${mo("−")}${mi("p")}`)}${mo("&#xA0;&#xA0;&#xA0;")}`
      + `${sub(mi("p"), "Levitt")}${mo("=")}<msup>${mn("0.5")}<mrow>${mn(1)}${mo("/")}${mi("n")}</mrow></msup>`),
    mathNote: "Kaernbach (left): steps for a target p; Levitt (right): the p an n-down/1-up rule converges to.",
    picture: sc("reversal"),
    scenarios: [
      { scenario: "Default", value: "Weighted, 75%", why: "every trial moves the staircase; middle of the 4-way E's range" },
      { scenario: "Patients who guess a lot", value: "Levitt, n = 3 (79.4%)", why: "a run of right answers needed before going fainter" },
      { scenario: "Fewest trials for the precision", value: "QUEST, stop when settled (SD 0.06, at most 30)", why: "about 85 trials with 4 staircases; SD about 0.03" },
    ],
    source: SIM_SOURCE,
  },
  "contrast.weighted_target": {
    title: "Target share right (Kaernbach)",
    what: "The share of right answers the weighted staircase settles at. Higher targets settle at stronger letters (lower "
      + "log CS) and feel easier to the patient; the score is the contrast at that share. With 4 choices the range runs "
      + "from 25% (guessing) to about 98% (lapses): stay well inside it.",
    math: m(`${sub(mi("Δ"), "up")}${mo("=")}${sub(mi("Δ"), "down")}${mo("·")}${frac(mi("p"), `${mn(1)}${mo("−")}${mi("p")}`)}`),
    mathNote: "p = 0.75 gives an up step 3× the down step.",
    range: "0.5–0.95; default 0.75.",
    picture: sc("step"),
    scenarios: [
      { scenario: "Default", value: "0.75", why: "the steepest part of the 4-way E's psychometric function" },
      { scenario: "Frustration-prone patients", value: "0.80", why: "more right answers; slightly stronger letters" },
    ],
  },
  "contrast.levitt_n_down": {
    title: "Right answers in a row before a step fainter (Levitt)",
    what: "The Levitt staircase steps fainter only after this many right answers in a row, and stronger after any wrong one.",
    math: m(`${mi("p")}${mo("=")}<msup>${mn("0.5")}<mrow>${mn(1)}${mo("/")}${mi("n")}</mrow></msup>`),
    mathNote: "n = 2: 70.7%; 3: 79.4%; 4: 84.1% right at convergence.",
    range: "2–4; default 3.",
    picture: sc("reversal"),
  },
  "contrast.start_log_cs_easy": {
    title: "Easiest start",
    what: "Where the easiest staircase starts (log CS; 0 is full contrast, black on white). The other staircases start "
      + "evenly spaced between this and the hardest start. Starting below the patient's threshold gives the easy, "
      + "confidence-building letters at the beginning; starting above gives a few misses first.",
    math: m(`${sub(mi("s"), "i")}${mo("=")}${sub(mi("s"), "easy")}${mo("+")}${frac(`${mi("i")}${mo("−")}${mn(1)}`, `${mi("n")}${mo("−")}${mn(1)}`)}`
      + `<mo>(</mo>${sub(mi("s"), "hard")}${mo("−")}${sub(mi("s"), "easy")}<mo>)</mo>`),
    range: "0–2.5 log CS; default 0.9.",
    picture: sc("start"),
    scenarios: [
      { scenario: "Typical adults (log CS 1.5–2.0)", value: "0.9 to 1.8", why: "brackets the normal range" },
      { scenario: "Low vision", value: "0.3 to 1.2", why: "starts where the patient can see; fewer wasted trials" },
    ],
  },
  "contrast.start_log_cs_hard": {
    title: "Hardest start",
    what: "Where the hardest staircase starts (log CS). Putting it a little above the expected threshold means one staircase "
      + "comes at the threshold from above, which guards against a staircase stuck below it.",
    range: "0–2.5 log CS; default 1.8.",
    picture: sc("start"),
    scenarios: [
      { scenario: "Typical adults", value: "1.8", why: "just above most healthy thresholds" },
      { scenario: "Low vision", value: "1.2", why: "keeps the hardest start within reach" },
    ],
  },
  "contrast.step_log_cs": {
    title: "First step",
    what: "How far a staircase moves after its first answers, in log units. Steps are halved at the 1st and 2nd reversal, "
      + "so big first steps cost little later. For QUEST, the fixed step down before its first error.",
    range: "0.05–0.6 log units; default 0.3.",
    picture: sc("step"),
    scenarios: [
      { scenario: "Default", value: "0.3", why: "reaches the threshold from 0.9 in 2–3 trials" },
      { scenario: "Starts close to the expected threshold", value: "0.15–0.2", why: "fewer overshoots" },
    ],
  },
  "contrast.discard_reversals": {
    title: "Reversals left out",
    what: "A reversal is a change of direction (fainter to stronger or back). Each staircase's threshold is the mean of its "
      + "reversal contrasts, leaving out its first few: those come from the walk down from the start, not from the threshold.",
    math: m(`${sub(mi("T"), "i")}${mo("=")}${mi("mean")}<mo>(</mo>${sub(mi("r"), "k")}<mo>)</mo>${mo(",")}${mo("&#xA0;")}${mi("k")}${mo(">")}${mi("d")}`),
    mathNote: "Shown dashed in the picture: the reversals left out.",
    range: "0–4; default 2.",
    picture: sc("reversal"),
  },
  "contrast.staircase_max_log_cs": {
    title: "Faintest contrast allowed",
    what: "No staircase asks for a fainter letter than this. The screen also limits it: below one code step a letter "
      + "cannot be drawn (the screen check records the display's ceiling: about log CS 2.65 at 10 bits, 2.05 at 8, 2.0 through HDR10).",
    range: "1.5–3.0 log CS; default 2.5.",
  },
  "contrast.stagger": {
    title: "Staggered starts",
    what: "None: all staircases start together. Fixed: they join one at a time, in random order, every few staircase "
      + "trials. Random: the same, with a random gap around that number.\n\nStaggering spreads the easy first letters of "
      + "each staircase across the test, instead of bunching them at the start, and keeps the patient from learning a pattern.",
    picture: sc("join"),
    scenarios: [
      { scenario: "Default", value: "None", why: "simplest; all staircases share the work from the first trial" },
      { scenario: "Many staircases (6+)", value: "Random, every 3", why: "fewer easy letters in a row at the start" },
    ],
  },
  "contrast.stagger_trials": {
    title: "Trials between joins",
    what: "With staggered starts, how many staircase trials pass before the next staircase joins (the mean, for random).",
    math: m(`${sub(mi("t"), "last join")}${mo("≈")}<mo>(</mo>${mi("n")}${mo("−")}${mn(1)}<mo>)</mo>${mo("×")}${mi("g")}`),
    range: "1–10; default 3.",
    picture: sc("join"),
  },
  "contrast.pick": {
    title: "Which staircase goes next",
    what: "Random: any running staircase, at random (one may get several turns in a row). Balanced: each running staircase "
      + "once per round, in random order, never the same one twice in a row: the staircases progress evenly.",
    picture: sc("pick"),
    scenarios: [
      { scenario: "Default", value: "Random", why: "classic interleaving" },
      { scenario: "Short tests", value: "Balanced", why: "no staircase falls behind" },
    ],
  },
  "contrast.stop_rule": {
    title: "When each staircase stops",
    what: "Trials: each runs its set number of trials. Stop when settled: each stops once it has enough reversals (up/down "
      + "types) or once QUEST's uncertainty is small enough; the trial count is then only an upper limit. Settled stopping "
      + "saves trials on steady patients and spends them on unsteady ones.",
    picture: sc("stop"),
    scenarios: [
      { scenario: "Default", value: "Trials", why: "same length every time" },
      { scenario: "Kaernbach, precise", value: "Settled, 6 reversals, at most 30", why: "about 77 trials with 4 staircases; SD about 0.04" },
      { scenario: "QUEST, precise", value: "Settled, SD 0.06, at most 30", why: "about 85 trials with 4 staircases; SD about 0.03" },
    ],
    source: SIM_SOURCE,
  },
  "contrast.stop_reversals": {
    title: "Reversals to stop (settled)",
    what: "An up/down staircase that stops when settled stops after this many reversals, counted after the ones left out.",
    range: "2–12; default 6.",
    picture: sc("stop"),
  },
  "contrast.stop_sd_log": {
    title: "Uncertainty to stop (QUEST, settled)",
    what: "A QUEST staircase that stops when settled stops once the SD of its estimate is this small (log units).",
    math: m(`${sub(mi("σ"), "posterior")}${mo("≤")}${sub(mi("σ"), "stop")}`),
    range: "0.02–0.3; default 0.08.",
    picture: sc("stop"),
  },
  "contrast.catch_rate": {
    title: "Attention checks",
    what: "A share of trials shown at a clearly visible contrast, fed to no staircase. A patient paying attention gets them "
      + "right; misses suggest lapses, guessing or a problem with the set-up. Never two in a row.",
    math: m(`${mi("E")}<mo>[</mo>${sub(mi("N"), "checks")}<mo>]</mo>${mo("=")}${mi("r")}${mo("·")}${mi("N")}`),
    mathNote: "Fewer than 3 checks are not judged.",
    range: "0–30%; default 0 (contrast), 10% (acuity).",
    picture: sc("catch"),
    scenarios: [
      { scenario: "Default", value: "0%", why: "every trial informs a staircase" },
      { scenario: "Unsupervised home use", value: "10%", why: "about 6 checks in a 60-trial test" },
    ],
  },
  "contrast.catch_log_cs": {
    title: "Attention-check contrast",
    what: "How strong the attention-check letters are (log CS; 0 = full contrast).",
    range: "0–1 log CS; default 0.",
  },
  "contrast.catch_min_pct": {
    title: "Attention checks right at least",
    what: "If fewer attention checks than this are right, the result is flagged (or made invalid, below).",
    range: "0–100%; default 80%.",
  },
  "contrast.catch_action": {
    title: "If too few attention checks are right",
    what: "Flag: the result counts, with a warning. Invalid: the result does not count and the test is repeated.",
  },
  "contrast.gate_trials": {
    title: "Size check: letters",
    what: "Before the contrast test, a few letters at full contrast and the test's size. All must be right: this makes "
      + "sure the patient can see the letters at all, so a low contrast score means low contrast sensitivity, not that the "
      + "letters are too small.",
    picture: (ctx) => { const c = contrastOf(ctx); return <GatePicture trials={c.gate_trials} stepLogmar={c.gate_step_logmar} />; },
    range: "2–8; default 4.",
  },
  "contrast.gate_step_logmar": {
    title: "Size check: growth after a miss",
    what: "If a size-check letter is missed, the letters grow by this much and the check repeats.",
    picture: (ctx) => { const c = contrastOf(ctx); return <GatePicture trials={c.gate_trials} stepLogmar={c.gate_step_logmar} />; },
    range: "0.05–0.3 logMAR; default 0.1.",
  },
  "contrast.gate_max_attempts": {
    title: "Size check: attempts",
    what: "After this many failed size checks the test stops without a contrast result.",
    range: "1–10; default 5.",
  },
  "contrast.pr_step_log_cs": {
    title: "Chart levels: step",
    what: "Contrast step between levels, as on the chart (0.15 log CS per triplet).",
    picture: (ctx) => { const c = contrastOf(ctx); return <ChartLevelsPicture step={c.pr_step_log_cs} max={c.pr_max_log_cs} group={c.pr_group_size} />; },
    range: "0.05–0.3; default 0.15.",
  },
  "contrast.pr_max_log_cs": {
    title: "Chart levels: faintest level",
    what: "The faintest level offered (log CS).",
    picture: (ctx) => { const c = contrastOf(ctx); return <ChartLevelsPicture step={c.pr_step_log_cs} max={c.pr_max_log_cs} group={c.pr_group_size} />; },
    range: "default 2.25.",
  },
  "contrast.pr_group_size": {
    title: "Chart levels: letters per level",
    what: "How many letters at each level, and (with the pass mark) how many must be right to pass it. The chart has 3 "
      + "letters per level and passes 2; with a 4-way E (25% guessing) 5 letters with 4 right guards better against lucky guesses.",
    math: m(`${mi("P")}<mo>(</mo>${mo("≥")}${mn(4)}${mo("/")}${mn(5)}${mo("|")}${mi("guessing")}<mo>)</mo>${mo("≈")}${mn("1.6%")}`),
    picture: (ctx) => { const c = contrastOf(ctx); return <ChartLevelsPicture step={c.pr_step_log_cs} max={c.pr_max_log_cs} group={c.pr_group_size} />; },
    range: "3–8; default 5.",
  },
  "contrast.quest_trials": { title: "QUEST pair: trials", what: "Trials per staircase for the older QUEST pair.", range: "default 30." },
  "contrast.quest_prior_mean": {
    title: "QUEST: prior guess",
    what: "Where QUEST expects the threshold before any answers (log10 Weber; −1.5 = log CS 1.5). With a broad prior it "
      + "matters little after a few trials.",
    picture: (ctx) => { const c = contrastOf(ctx); return <PriorPicture mean={c.quest_prior_mean} sd={c.quest_prior_sd} lo={-3} hi={0} unit="log Weber" />; },
  },
  "contrast.quest_prior_sd": {
    title: "QUEST: prior uncertainty",
    what: "How unsure QUEST is about the threshold before any answers (SD, log units). Too narrow and a patient far from "
      + "the guess takes many trials to reach; broad is safe.",
    picture: (ctx) => { const c = contrastOf(ctx); return <PriorPicture mean={c.quest_prior_mean} sd={c.quest_prior_sd} lo={-3} hi={0} unit="log Weber" />; },
    scenarios: [{ scenario: "Default", value: "0.8", why: "covers healthy and low-vision thresholds" }],
  },
  "contrast.quest_beta": {
    title: "QUEST: slope",
    what: "How sharply the chance of a right answer rises around the threshold (per log unit). QUEST assumes it; a wrong "
      + "slope costs efficiency more than accuracy.",
    math: m(`${mi("ψ")}<mo>(</mo>${mi("x")}<mo>)</mo>${mo("=")}${mi("γ")}${mo("+")}${frac(`${mn(1)}${mo("−")}${mi("γ")}${mo("−")}${mi("δ")}`, `${mn(1)}${mo("+")}<msup>${mi("e")}<mrow>${mo("−")}${mi("β")}<mo>(</mo>${mi("x")}${mo("−")}${mi("T")}<mo>)</mo></mrow></msup>`)}`),
    mathNote: "x: the letter's log contrast; T: the threshold; γ: guessing; δ: lapses.",
    picture: (ctx) => { const c = contrastOf(ctx); return <PsychometricPicture beta={c.quest_beta} gamma={c.quest_gamma} delta={c.quest_delta} mark="beta" unit="log units" />; },
    range: "default 15.",
  },
  "contrast.quest_gamma": {
    title: "QUEST: guess rate",
    what: "The chance of a right answer by guessing: 0.25 for a 4-way E.",
    picture: (ctx) => { const c = contrastOf(ctx); return <PsychometricPicture beta={c.quest_beta} gamma={c.quest_gamma} delta={c.quest_delta} mark="gamma" unit="log units" />; },
    range: "0.25 for the E.",
  },
  "contrast.quest_delta": {
    title: "QUEST: lapse rate",
    what: "The chance of a wrong answer to a clearly visible letter (a blink, a slip of the finger). Allowing for it keeps "
      + "one slip from dragging the estimate.",
    picture: (ctx) => { const c = contrastOf(ctx); return <PsychometricPicture beta={c.quest_beta} gamma={c.quest_gamma} delta={c.quest_delta} mark="delta" unit="log units" />; },
    range: "0.01–0.1; default 0.04.",
  },
  "contrast.quest_grain": { title: "QUEST: grid step", what: "The resolution of QUEST's internal grid of possible thresholds (log units). 0.01 is fine; coarser is faster but blockier.", range: "default 0.01." },
};
